import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CalloutNotification, DateTimeFormat, Link, SearchField, Table, Text, useLocalizeMessage, } from 'libs.nucleus';
import { DCR_LIST_PAGE_SIZE } from '../../constants';
import { useDataChangeRequestsContext, useDcrApiContext } from '../../contexts';
import { useDcrList } from '../../hooks';
import { getDcrTypeLabel } from '../../utils';
import { DcrStatusBadge } from '../dcr_status_badge';
export const DcrTable = ({ headerContent }) => {
    const translate = useLocalizeMessage();
    const apiContext = useDcrApiContext();
    const { basePath } = useDataChangeRequestsContext();
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DCR_LIST_PAGE_SIZE });
    const [sorting, setSorting] = useState([{ id: 'dcr_intake__created_date', desc: true }]);
    // What has been typed and what has been asked for are kept apart so a query only leaves the
    // browser when the reader presses enter or clears the field
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const { data, isLoading, error } = useDcrList(apiContext, {
        limit: pagination.pageSize,
        offset: pagination.pageIndex * pagination.pageSize,
        ...(sorting[0] && { orderBy: sorting[0].id, order: sorting[0].desc ? 'desc' : 'asc' }),
        ...(search && { search }),
    });
    const applySearch = (value = '') => {
        setSearch(value.trim());
        setPagination((current) => ({ ...current, pageIndex: 0 }));
    };
    const columns = useMemo(() => [
        {
            header: translate('ID'),
            accessorKey: 'dcr_intake__number',
            cell: ({ row }) => (_jsx(Link, { LinkComponent: RouterLink, to: `${basePath}/${row.original._id}`, children: row.original.dcr_intake__number })),
        },
        {
            header: translate('Participant'),
            accessorKey: 'dcr_intake__public_user_number',
        },
        {
            header: translate('Type'),
            accessorKey: 'dcr_intake__type',
            cell: ({ getValue }) => (_jsx(Text, { size: 'sm', className: 'whitespace-normal', children: getDcrTypeLabel(getValue()) })),
        },
        {
            header: translate('Created by'),
            accessorKey: 'dcr_intake__created_by_name',
            enableSorting: false,
        },
        {
            header: translate('Creation date'),
            accessorKey: 'dcr_intake__created_date',
            cell: ({ getValue }) => {
                const value = getValue();
                return value ? _jsx(DateTimeFormat, { date: value }) : null;
            },
        },
        {
            header: translate('Status'),
            accessorKey: 'dcr_intake__status',
            cell: ({ getValue }) => _jsx(DcrStatusBadge, { status: getValue() }),
        },
        {
            header: translate('Action'),
            accessorKey: 'dcr_intake__action',
            enableSorting: false,
        },
        {
            header: translate('Note'),
            accessorKey: 'dcr_intake__note',
            enableSorting: false,
            cell: ({ getValue }) => (_jsx(Text, { size: 'sm', className: 'whitespace-normal', children: getValue() ?? '' })),
        },
    ], [basePath, translate]);
    if (error) {
        // Without this a rejected session or an org role the connector does not accept would be
        // indistinguishable from a study that has no requests
        return (_jsx(CalloutNotification, { type: 'error', title: translate('Could not load data change requests'), subtitle: error?.response?.data?.reason ??
                translate('Check that your account has access to this environment, then try again.') }));
    }
    return (_jsx(Table, { columns: columns, data: data?.records ?? [], getRowId: '_id', isLoading: isLoading, labelNoResults: search ? translate('No data change requests match this search') : translate('No data change requests yet'), customHeaderContent: _jsxs("div", { className: 'flex flex-1 items-end gap-3', children: [_jsx(SearchField, { ariaLabel: translate('Search data change requests'), placeholder: translate('Search by ID, participant, type or status'), width: 'lg', value: searchInput, onChange: (event) => setSearchInput(event.target.value), onSearch: (value) => applySearch(value), onClear: () => {
                        setSearchInput('');
                        applySearch();
                    } }), headerContent] }), hasPagination: true, initialPageSize: DCR_LIST_PAGE_SIZE, paginationNavigationScreenReaderLabel: translate('Data change requests table navigation'), manualPagination: true, pageCount: Math.ceil((data?.totalSize ?? 0) / pagination.pageSize), manualPaginationState: pagination, onPaginationChange: setPagination, enableSorting: true, manualSorting: true, manualSortingState: sorting, onSortingChange: setSorting }));
};
